import type { UiLang, TravelMode, Pace, TripForm, VehicleKind, Vibe } from "./types";

/*
 * Rekkefølgen brukeren møter dem i, standarden først.
 *
 * `kollektiv` MÅ stå her og ikke bare i velgeren. Å legge den til ett sted og
 * ikke det andre er ikke en type-feil — en `TravelMode[]` rommer gjerne en
 * delmengde — så lenka ville blitt skrevet som `reise=kollektiv`, hvitelista
 * her ville avvist den, og turen ville stille endret seg ved omlasting og ved
 * deling.
 */
const MODES: TravelMode[] = ["kollektiv", "tog", "buss", "bil"];
const VEHICLES: VehicleKind[] = ["bil", "bobil", "motorsykkel"];
const VIBES: Vibe[] = ["fjell", "fjord", "by", "miks"];
const PACES: Pace[] = ["avslappet", "aktiv"];

/**
 * ── Turlengden, og den ene regelen som binder to felter sammen ─────────────
 *
 * Speiler `MIN_DAYS`, `MIN_FOCUS_DAYS` og `MAX_DAYS` i
 * api/src/routes/validate.ts. Tallene sto bare på serveren før, mens
 * grensesnittet aldri kom forbi de fire forslagene `dayOptions` gir — så en som
 * ville ha åtte dager på en kort rute ikke hadde noen knapp å trykke på, selv
 * om serveren ville tatt imot svaret.
 *
 * De bor her og ikke i `ControlPanel` fordi det ikke er skjemaets regler. En
 * delt lenke går utenom skjemaet, fritekstplanleggeren fyller det ut utenfra,
 * og en lagret plan hentes fram uten at noen har trykket på noe. Alle tre må
 * svare på det samme spørsmålet, og et lag som eier svaret er et lag alle kan
 * spørre.
 */
export const MIN_DAYS = 2;
export const MAX_DAYS = 12;

/**
 * Gulvet når planen handler om ett sted.
 *
 * Toeren er en regel om *korridorer* — ingen kjører Oslo–Bergen på en dag.
 * Trolltunga på en dag er derimot det vanlige, tolv timer opp og ned.
 */
export const MIN_FOCUS_DAYS = 1;

/** Det laveste dagtallet et skjema kan be om. */
export function floorFor(form: Pick<TripForm, "focusStopId">): number {
  return form.focusStopId ? MIN_FOCUS_DAYS : MIN_DAYS;
}

/**
 * Skjemaet slik serveren kan svare på det.
 *
 * ── Hva som ble meldt ──────────────────────────────────────────────────────
 *
 * «"days" must be a number between 2 and 12.» — en rød feilmelding over et
 * skjema brukeren ikke hadde gjort noe galt med.
 *
 * ── Hvordan den oppsto ─────────────────────────────────────────────────────
 *
 * Dagtallet og fokuset er to felter som kan endres hver for seg, og de har en
 * regel som binder dem sammen. Veien inn i feilen: velg Trolltunga, sett én
 * dag, trykk «hele ruta igjen». Fokuset ble borte, dagtallet ble stående på 1,
 * og skjemaet ba om noe serveren med rette avviste. `handleCorridor` hadde det
 * samme hullet, fra den andre kanten.
 *
 * ── Hvorfor det er én funksjon og ikke en rettelse i hver knapp ────────────
 *
 * Fordi det var nettopp «rett det i knappene som endrer det» som skapte
 * feilen. Det finnes minst seks veier inn i skjemaet — de to knappene,
 * rutevelgeren, fritekstplanleggeren, en delt lenke, og en plan hentet fram
 * fra lagringen — og en regel som må huskes på seks steder er en regel som blir
 * glemt på det sjuende.
 *
 * `generate` er stedet alle seks møtes, og der kalles denne. Samme grunn som at
 * rullingen *observerer* layout framfor å telle opp hva som kan flytte seg; se
 * lib/scroll.ts.
 *
 * Returnerer skjemaet uendret når det alt er brukbart, så kalleren kan bruke
 * identitet til å se om noe ble rettet.
 */
export function usableForm(form: TripForm): TripForm {
  const floor = floorFor(form);
  return form.days >= floor ? form : { ...form, days: floor };
}

export const DEFAULT_CORRIDOR = "bergensbanen";

export const DEFAULT_FORM: TripForm = {
  corridorId: DEFAULT_CORRIDOR,
  days: 4,
  /*
   * Kollektivt er standarden. En lenke uten `reise=` er trygg fordi
   * `formToQuery` alltid har skrevet parameteren — så ingen delt lenke
   * mangler den, og ingen gammel lenke arver den nye standarden.
   */
  mode: "kollektiv",
  vehicle: "bil",
  vibe: "miks",
  pace: "avslappet",
  startDate: "",
  optimizeForWeather: false,
  suggestBestDay: false,
  include: [],
  exclude: [],
  fromStopId: "",
  focusStopId: "",
  returnToStopId: "",
  ownerName: "",
};

/**
 * Hvor mange tegn et navn i en delt lenke får være.
 *
 * Et fornavn, ikke en hilsen. Taket står her og ikke bare i inntastingsfeltet,
 * fordi en URL kan skrives for hånd — og en overskrift som er tre linjer lang
 * er ikke lenger en overskrift.
 */
const MAX_NAME_LENGTH = 24;

/**
 * Kontrolltegn: C0-området, DEL, og C1-området.
 *
 * Skrevet som en variabel med `\u`-escapes framfor rett inn i regexen fordi de
 * *er* usynlige — en tidligere runde skrev dem inn som faktiske tegn i
 * kildekoden ved et uhell, og en tegnklasse som ser tom ut på skjermen er noe
 * ingen kan se er feil.
 */
const CONTROL_CHARS = new RegExp("[\\u0000-\\u001f\\u007f-\\u009f]", "g");

/**
 * Navnet fra URL-en, renset.
 *
 * Kontrolltegn og linjeskift byttes til mellomrom framfor å bli vist. Dette er
 * ikke en XSS-sperre — React escaper det som rendres — det er at et navn med et
 * linjeskift i midten ødelegger oppsettet, og at et null-tegn ikke er et fornavn.
 */
function cleanName(value: string | null): string {
  if (!value) return "";
  return value
    .replace(CONTROL_CHARS, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_NAME_LENGTH);
}

/** Samme rensing, for det brukeren skriver inn. Eksportert til inntastingen. */
export function normaliseOwnerName(value: string): string {
  return cleanName(value);
}

/** Leser en kommaseparert liste med stopp-slugs fra URL-en. */
function idList(value: string | null): string[] {
  if (!value) return [];
  return value
    .split(",")
    .map((id) => id.trim())
    .filter((id) => /^[a-z]+$/.test(id))
    .slice(0, 20);
}

/**
 * Leser skjemaet fra URL-en, slik at en generert rute kan deles som lenke
 * og gjenskapes nøyaktig hos mottakeren.
 */
export function formFromUrl(search: string): TripForm {
  const params = new URLSearchParams(search);
  const days = Number(params.get("dager"));
  const mode = params.get("reise") as TravelMode;
  const vehicle = params.get("kjoretoy") as VehicleKind;
  const vibe = params.get("type") as Vibe;
  const pace = params.get("tempo") as Pace;
  const startDate = params.get("dato") ?? "";
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(startDate);
  const corridorId = params.get("rute");

  return {
    // Korridor-id-en valideres av serveren; ugyldige verdier faller tilbake der.
    corridorId: corridorId && /^[a-z-]+$/.test(corridorId) ? corridorId : DEFAULT_CORRIDOR,
    /*
     * Opptil tolv, som er det serveren tar imot.
     *
     * Sto som `days <= 5` og var et etterlatt tak: en delt lenke på åtte dager
     * ble stille satt til fire, så mottakeren fikk en annen tur enn den som
     * ble delt — uten at noe sa fra. Se MIN_DAYS/MAX_DAYS i api.
     */
    /*
     * Én dag er gyldig, men bare for en plan om ett sted. Toeren er en regel om
     * korridorer — ingen kjører Oslo-Bergen på en dag — og en delt lenke til en
     * dagstur til Trolltunga skulle ikke bli til to dager hos mottakeren. Se
     * `MIN_FOCUS_DAYS` i api/src/routes/validate.ts.
     */
    days:
      days >= (params.get("sted") ? MIN_FOCUS_DAYS : MIN_DAYS) && days <= MAX_DAYS
        ? days
        : DEFAULT_FORM.days,
    mode: MODES.includes(mode) ? mode : DEFAULT_FORM.mode,
    vehicle: VEHICLES.includes(vehicle) ? vehicle : DEFAULT_FORM.vehicle,
    vibe: VIBES.includes(vibe) ? vibe : DEFAULT_FORM.vibe,
    pace: PACES.includes(pace) ? pace : DEFAULT_FORM.pace,
    startDate: validDate ? startDate : "",
    optimizeForWeather: params.get("vaer") === "1" && validDate,
    suggestBestDay: params.get("bestedag") === "1" && validDate,
    // Valideres av serveren mot de kjente stoppene; et ukjent id gir 400 der.
    fromStopId: (params.get("fra") ?? "").replace(/[^a-z-]/g, "").slice(0, 40),
    // Samme rensing og samme grunn: serveren avviser et ukjent id med 400.
    focusStopId: (params.get("sted") ?? "").replace(/[^a-z-]/g, "").slice(0, 40),
    returnToStopId: (params.get("tilbake") ?? "").replace(/[^a-z-]/g, "").slice(0, 40),
    include: idList(params.get("lagt_til")),
    exclude: idList(params.get("fjernet")),
    ownerName: cleanName(params.get("navn")),
  };
}

export function formToQuery(form: TripForm, lang: UiLang): string {
  const params = new URLSearchParams({
    rute: form.corridorId,
    dager: String(form.days),
    reise: form.mode,
    type: form.vibe,
    tempo: form.pace,
    lang,
  });
  // Bare når det ikke er vanlig bil: standardverdier hører ikke hjemme i en delt lenke.
  if (form.vehicle !== DEFAULT_FORM.vehicle) params.set("kjoretoy", form.vehicle);
  if (form.startDate) params.set("dato", form.startDate);
  if (form.optimizeForWeather) params.set("vaer", "1");
  if (form.suggestBestDay) params.set("bestedag", "1");
  if (form.include.length) params.set("lagt_til", form.include.join(","));
  if (form.exclude.length) params.set("fjernet", form.exclude.join(","));
  // Bare når noen har oppgitt et navn. En tom `navn=` i lenka er støy.
  // Bare når man ikke starter der ruta starter, som er normaltilstanden.
  if (form.fromStopId) params.set("fra", form.fromStopId);
  /*
   * Bare når planen handler om ett sted. Uten dette ville en delt lenke til en
   * Trolltunga-plan åpnet seg som hele Stavanger–Bergen hos mottakeren — altså
   * nettopp den planen avsenderen valgte bort.
   */
  if (form.focusStopId) params.set("sted", form.focusStopId);
  /*
   * Hjemreisen tar en dag av turen når den fyller en. Uten den i lenka ville
   * mottakeren fått en plan med én dag mer enn avsenderen ser — og ingen
   * forklaring på hvor den kom fra.
   */
  if (form.returnToStopId) params.set("tilbake", form.returnToStopId);
  if (form.ownerName) params.set("navn", form.ownerName);
  return `?${params.toString()}`;
}

/** Oppdaterer adresselinjen uten å legge til historikk-oppføringer. */
export function syncUrl(form: TripForm, lang: UiLang): void {
  window.history.replaceState(null, "", formToQuery(form, lang));
}

/**
 * Hele lenka til turen, slik den kan limes inn et annet sted.
 *
 * `formToQuery` gir bare spørringen, som er alt adresselinjen trenger — men en
 * lenke man sender til noen må ha vertsnavnet med. Den finnes her framfor i
 * komponenten slik at det bare er ett sted som vet hvordan en delt lenke ser ut.
 */
export function shareUrl(form: TripForm, lang: UiLang): string {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}${formToQuery(form, lang)}`;
}

/** true når URL-en faktisk inneholder et lagret skjema. */
export function hasSharedForm(search: string): boolean {
  return new URLSearchParams(search).has("dager");
}

/**
 * Navnet på den som delte turen, når lenka kom fra noen andre.
 *
 * Tom streng både når lenka ikke har et navn og når den ikke er en delt lenke
 * i det hele tatt. Skillet betyr noe: uten `dager` er dette brukerens egen
 * økt, og da er navnet i feltet deres eget — ikke en avsender å opplyse om.
 */
export function sharedByName(search: string): string {
  if (!hasSharedForm(search)) return "";
  return cleanName(new URLSearchParams(search).get("navn"));
}
